import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { UseFormGetValues } from 'react-hook-form';

import { RootState } from '../../../../../core/state/state';
import { BookingModalType } from '../../../../../core/state/bookings/types';
import { DATE_FORMAT_EU_DOT, DATE_FORMAT_HOURS_MINUTES } from '../../../../../core/constants/date';
import DeliveryZoneBadge from '../../../../components/delivery-zone-badge/DeliveryZoneBadge';
import { selectProjectSettings } from '../../../../../core/state/bookingProjectSettings/selectors';
import { CreateBookingExternalFormData } from '../types';
import { getTimeDate } from '../../../../../core/utils/date';
import { isMobileScreenSize } from '../../../../../core/utils/isMobileScreenSize';
import { Size } from '../../../../../core/enums/Size';

import './CreateExternalStepThree.scss';

interface Props {
  getValues: UseFormGetValues<CreateBookingExternalFormData>;
  onSubmit: any;
  handleSubmit: any;
}

const CreateExternalStepThree: React.FC<Props> = ({ getValues, handleSubmit, onSubmit }) => {
  const { t } = useTranslation();
  const isMobileScreen = isMobileScreenSize();

  const { isOpen: isAnyModalOpen, data } = useSelector((state: RootState) => state.bookings.modal);
  const isOpen = isAnyModalOpen && data?.type === BookingModalType.External;

  const contacts = useSelector((state: RootState) => state.subcontractor.contacts);
  const userName = useSelector((state: RootState) => state.auth.authInfo?.account.name ?? '');
  const project = useSelector((state: RootState) => state.projects.projectDetails);
  const projectSettings = useSelector(selectProjectSettings);
  const { bookingProjectEquipment: transportEquipment } = projectSettings;

  if (!isOpen) {
    return <></>;
  }

  const { date, startTime, endTime, contactPersonId, deliveryDuration, description, transportEquipmentId, deliveryZoneId } = getValues();

  const contact = contacts?.find((item) => item.id === Number(contactPersonId));
  const equipment = transportEquipment?.find((item) => item.id === Number(transportEquipmentId));

  return (
    <form
      id="create-booking-form"
      className={`create-booking-modal ${isMobileScreen ? 'create-external-step-three--mobile' : 'overflow-auto'}`}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="create-booking-modal__content">
        <label className="input__label" htmlFor="additional-title">
          {t('booking.title')}
        </label>
        <p className="input__label create-booking-modal__title">{userName}</p>
        <div className="form-row">
          <div>
            <div className="input__label">{t('booking.deliveryDate')}</div>
            <div className="input__text">{date && format(new Date(date), DATE_FORMAT_EU_DOT)}</div>
          </div>
          <div>
            <div className="input__label">{t('booking.deliveryTime')}</div>
            <div className="input__text">
              {startTime && format(getTimeDate(startTime), DATE_FORMAT_HOURS_MINUTES)} -&nbsp;
              {endTime && format(getTimeDate(endTime), DATE_FORMAT_HOURS_MINUTES)}
            </div>
          </div>
        </div>
        <div className={!isMobileScreen ? 'form-row' : ''}>
          <div>
            <div className="input__label">{t('booking.deliveryZone')}</div>
            <div className="input__text">
              <DeliveryZoneBadge labelSize={Size.Medium} id={Number(deliveryZoneId)} />
            </div>
          </div>
          <div>
            <div className="input__label create-booking-modal__transport">{t('booking.transport')}</div>
            <div className="input__text">{equipment?.name}</div>
          </div>
        </div>
        <div className="form-row">
          <div>
            <div className="input__label">{t('booking.deliveryDuration')}</div>
            <div className="input__text">{deliveryDuration} h</div>
          </div>
          <span />
        </div>
        <div className="form-row">
          <div>
            <div className="input__label">{t('booking.contactPersonSubcontractor')}</div>
            {contact && (
              <div className="input__text">{`${contact.firstName} ${contact.lastName} ${
                contact.mobilePhoneNumber ? contact.mobilePhoneNumber : ''
              }`}</div>
            )}
          </div>
        </div>
        <div className="form-row">
          <div>
            <div className="input__label">{t('booking.description')}</div>
            <div className="input__text">{description}</div>
          </div>
        </div>
        <div className="form-row">
          <div>
            <div className="input__label">{t('booking.contactPersonKoester')}</div>

            {project?.projectContacts &&
              project.projectContacts.map((projectContact) => (
                <div
                  key={projectContact.id}
                  className="input__text"
                >{`${projectContact.firstName} ${projectContact.lastName} ${projectContact.mobilePhoneNumber}`}</div>
              ))}
          </div>
        </div>
        <div className="form-row">
          <div>
            <div className="input__label">{t('projectSettings.projectInfo.address')}</div>
            <div className="input__text">{project?.constructionSite.address}</div>
          </div>
        </div>
        <div className="form-row">
          <div>
            <div className="input__label">{t('projectSettings.projectInfo.deliveryNotes')}</div>
            <div className="input__text">{project?.constructionSite.deliveryNotes}</div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default CreateExternalStepThree;
